/**
 * Custom yup function to check if user selected a placeholder value
 *
 * Fails validation if the message parameter passed matches a field placeholder value.
 */
import * as Yup from 'yup';

import type { FieldEntityProps } from '@shared/types';

const preferredContactRequirePhoneValues = ['Phone', 'Call', 'Text'];

export default function getFieldValidationSchema({
  fields,
}: {
  fields: FieldEntityProps[];
}): {
  FormSchema: Yup.SchemaOf<unknown>;
  requiredFields: string[];
  placeholders: string[];
} {
  /**
   * Custom yup function to check if user selected a placeholder value
   *
   * Fails validation if the message parameter passed matches a field placeholder value.
   */
  Yup.addMethod<Yup.StringSchema>(
    Yup.string,
    'checkForPlaceholder',
    function (message: string) {
      return this.test('checkForPlaceholder', message, (value, ctx) => {
        if (value === 'Phone' && ctx.path === 'preferred_contact') {
          return true;
        }

        return value ? !placeholders.includes(value) : false;
      });
    },
  );

  /**
   * Custom yup function to check if user entered more than two words.
   *
   * Passes validation if the message parameter passed has more than two words, with a space after each word,
   * at least one word is two characters long, and they started to type a third word.
   */
  Yup.addMethod<Yup.StringSchema>(
    Yup.string,
    'validateMessageField',
    function (message: string) {
      return this.test('validateMessageField', message, (value) => {
        const matchMoreThanOneSpace = /\s+/g;
        const matchNewLineChars = /\n/g;

        if (value) {
          const noLineBreakAndOnlySingleSpacesInValue = value
            .replace(matchMoreThanOneSpace, ' ')
            .replace(matchNewLineChars, ' ');
          const splitWords = noLineBreakAndOnlySingleSpacesInValue.split(' ');
          const wordsLongerThanTwoChars = splitWords.some(
            (word) => word.length >= 2,
          );
          const moreThanTwoWord = splitWords.length > 2;
          const thirdWordHasLength = !!splitWords[2] && !!splitWords[2].length;

          return (
            wordsLongerThanTwoChars && moreThanTwoWord && thirdWordHasLength
          );
        }

        return false;
      });
    },
  );

  // fields in this object are never hidden from the user when using this layout.
  // if you need to have two forms share a layout but one form needs the field shown and the other needs it hidden,
  // refer to th conditional validation logic below.
  const neverHiddenFieldsValidation = {
    first_name: Yup.string()
      .min(2, 'First Name is too short, minimum 2 letters')
      .max(50, 'First Name is too long, minimum 50 letters')
      .required('Please enter your first name.'),
    last_name: Yup.string()
      .min(2, 'Last Name is too short, minimum 2 letters')
      .max(50, 'Last Name is too long, minimum 50 letters')
      .required('Please enter your last name.'),
    email: Yup.string()
      .email('Must be a valid email address...')
      .required('Please enter a valid email address.'),
    phone: Yup.string().when(['preferred_contact'], {
      is: (pref: string) => preferredContactRequirePhoneValues.includes(pref),
      then: Yup.string()
        .required('Please enter a valid phone number.')
        .min(10, 'Phone numbers must be minimum of 10 numbers')
        .test(
          'intl-tel-valid',
          'Invalid phone number',
          function () {
            // Check the _phoneInvalid flag set by IntlTelInput validation
            // Use strict equality to ensure fail-closed: undefined !== false
            return this.parent._phoneInvalid === false;
          },
        ),
    }),
    preferred_contact: Yup.string()
      .checkForPlaceholder('Please select a preferred contact method.')
      .required('Please select a preferred contact method.'),
    sms_opt_in: Yup.boolean().when(['preferred_contact'], {
      is: (pref: string) => pref === 'Text',
      then: Yup.bool()
        .required('Please opt in.')
        .oneOf([true], 'Please opt in.'),
    }),
  };
  // conditional validation logic for program_specialization field
  const programSpecializationValidation = {
    program_specialization: Yup.string()
      .checkForPlaceholder('Please select a program of interest.')
      .required('This field is required.'),
  };
  const isProgramSpecializationRequiredAndNotHidden = fields.some((field) => {
    return (
      field.parameterName === 'program_specialization' &&
      !field.isHidden &&
      field.rules.indexOf('required') !== -1
    );
  });
  // conditional validation logic for program_type field
  const programTypeValidation = {
    program_type: Yup.string()
      .checkForPlaceholder('Please select a program type.')
      .required('This field is required.'),
  };
  const isProgramTypeRequiredAndNotHidden = fields.some((field) => {
    return (
      field.parameterName === 'program_type' &&
      !field.isHidden &&
      field.rules.indexOf('required') !== -1
    );
  });
  // conditional validation logic for message field
  const messageValidation = {
    message: Yup.string()
      .max(2500, 'Message is too long, maximum 2500 letters')
      .validateMessageField('Please enter a message')
      .required('Please enter a message.'),
  };
  const isMessageRequiredAndNotHidden = fields.some((field) => {
    return (
      field.parameterName === 'message' &&
      !field.isHidden &&
      field.rules.indexOf('required') !== -1
    );
  });
  // conditional validation logic for application_semester field
  const applicationSemesterValidation = {
    application_semester: Yup.string()
      .checkForPlaceholder('Please select a semester.')
      .required('Please select a semester.'),
  };
  const isApplicationSemesterRequiredAndNotHidden = fields.some((field) => {
    return (
      field.parameterName === 'application_semester' &&
      !field.isHidden &&
      field.rules.indexOf('required') !== -1
    );
  });
  // conditional validation logic for degree_from_non_us_institution field
  const nonUsDegreeValidation = {
    degree_from_non_us_institution: Yup.string()
      .checkForPlaceholder('Please indicate a non us degree.')
      .required('Please indicate a non us degree.'),
  };
  const isNonUsDegreeRequiredAndNotHidden = fields.some((field) => {
    return (
      field.parameterName === 'degree_from_non_us_institution' &&
      !field.isHidden &&
      field.rules.indexOf('required') !== -1
    );
  });

  // todo add getFormSchema to forms endpoints -
  //  Call fields service get array of rules (eventually messages) to map to Yup validation.
  //  Ideally replace current field validation in Fields Service with Yup.
  const FormSchema = Yup.object().shape({
    ...neverHiddenFieldsValidation,
    ...(isProgramSpecializationRequiredAndNotHidden &&
      programSpecializationValidation),
    ...(isProgramTypeRequiredAndNotHidden && programTypeValidation),
    ...(isMessageRequiredAndNotHidden && messageValidation),
    ...(isApplicationSemesterRequiredAndNotHidden &&
      applicationSemesterValidation),
    ...(isNonUsDegreeRequiredAndNotHidden && nonUsDegreeValidation),
  });

  // get required fields from validation schema
  const requiredFields: string[] = [];
  const parameterSchema = FormSchema.pick(fields.map((f) => f.parameterName));

  for (const parameterName in parameterSchema.fields) {
    const isRequired =
      parameterSchema.fields[parameterName].exclusiveTests.required;

    if (isRequired) {
      requiredFields.push(parameterName);
    }
  }

  // get placeholders from values for custom validation function checkForPlaceholder
  const placeholders = fields.map((field) => {
    const isRequired = requiredFields.includes(field.parameterName);

    return isRequired ? `${field.name}*` : field.name;
  });

  return { FormSchema, requiredFields, placeholders };
}
